import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'hr', 'blockquote', 'pre', 'code',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'a', 'strong', 'em', 'b', 'i', 'u', 's', 'del', 'ins', 'mark', 'sub', 'sup', 'small',
  'span', 'div', 'section', 'article', 'header', 'footer', 'nav', 'aside', 'main',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  'img', 'figure', 'figcaption', 'picture', 'source', 'video', 'audio',
  'iframe',
  'details', 'summary',
  'abbr', 'cite', 'time', 'address',
];

const ALLOWED_ATTR = [
  'href', 'target', 'rel', 'title', 'alt', 'src', 'srcset', 'sizes',
  'width', 'height', 'loading', 'decoding',
  'class', 'id', 'style',
  'colspan', 'rowspan', 'scope', 'headers',
  'allow', 'allowfullscreen', 'frameborder', 'sandbox',
  'datetime', 'open', 'type', 'media',
  'data-*',
];

const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

// Only well-known video embed providers may be framed. Any other iframe is
// dropped, so untrusted content cannot frame arbitrary (or same-origin) pages.
const ALLOWED_FRAME_HOSTS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
  'player.vimeo.com',
  'www.loom.com',
  'fast.wistia.net',
];

let frameHookInstalled = false;

function installFrameHook(): void {
  if (frameHookInstalled || typeof DOMPurify.addHook !== 'function') return;
  frameHookInstalled = true;
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    const element = node as Element;
    const src = element.getAttribute?.('src') ?? '';
    let allowed = false;
    try {
      const parsed = new URL(src, window.location.origin);
      allowed =
        (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
        ALLOWED_FRAME_HOSTS.includes(parsed.hostname.toLowerCase());
    } catch {
      allowed = false;
    }
    if (!allowed) {
      element.remove?.();
    }
  });
}

export function sanitizeHtml(dirty: string): string {
  installFrameHook();
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ['target'],
  });
}

export function sanitizeInlineHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'u', 'a', 'span', 'br', 'mark', 'code', 'sub', 'sup', 'small'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    ALLOWED_URI_REGEXP,
  });
}
